/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useIntersectionObserver } from '@/hooks/use-intersection-observer';
import type { APlusCarousel, ContentAsset } from '@/lib/api/content-asset.server';

export type APlusContentAsset =
    | { html: ContentAsset['html']; aPlusCarousel?: never }
    | { html?: never; aPlusCarousel: NonNullable<ContentAsset['aPlusCarousel']> };

function aspectRatio(image: { width?: number; height?: number }, fallback: string) {
    return image.width && image.height ? `${image.width} / ${image.height}` : fallback;
}

function StructuredCarousel({ content, loadImages }: { content: APlusCarousel; loadImages: boolean }) {
    const [activeSlide, setActiveSlide] = useState(0);
    const slideRefs = useRef<Array<HTMLLIElement | null>>([]);

    const showSlide = (index: number) => {
        const nextIndex = Math.min(Math.max(index, 0), content.slides.length - 1);
        setActiveSlide(nextIndex);
        slideRefs.current[nextIndex]?.scrollIntoView?.({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
        });
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'ArrowLeft') showSlide(activeSlide - 1);
        if (event.key === 'ArrowRight') showSlide(activeSlide + 1);
        if (event.key === 'Home') showSlide(0);
        if (event.key === 'End') showSlide(content.slides.length - 1);
    };

    return (
        // RGBARRY-CUSTOMIZATION: keep merchant-authored A+ media inside the PDP column at every viewport width.
        <section className="w-full min-w-0 max-w-full overflow-x-clip" aria-label={content.title}>
            <div className="mx-auto mb-6 max-w-3xl text-center">
                <h2 className="text-2xl font-semibold md:text-3xl">{content.title}</h2>
                {content.description ? <p className="mt-3 text-base">{content.description}</p> : null}
            </div>

            <div className="relative min-[576px]:hidden">
                <ul
                    className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                    aria-label="A+ feature slides"
                    aria-live="polite"
                    onScroll={(event) => {
                        const width = event.currentTarget.clientWidth;
                        if (width > 0) {
                            setActiveSlide(
                                Math.min(Math.round(event.currentTarget.scrollLeft / width), content.slides.length - 1)
                            );
                        }
                    }}>
                    {content.slides.map((slide, index) => (
                        <li
                            className="w-full shrink-0 snap-center"
                            key={slide.src}
                            style={{ aspectRatio: aspectRatio(slide, '351 / 564') }}
                            ref={(element) => {
                                slideRefs.current[index] = element;
                            }}
                            aria-label={`${index + 1} of ${content.slides.length}`}>
                            {loadImages ? (
                                <img
                                    className="mx-auto h-auto max-w-full"
                                    src={slide.src}
                                    alt={slide.alt}
                                    width={slide.width}
                                    height={slide.height}
                                    loading="lazy"
                                    decoding="async"
                                />
                            ) : null}
                        </li>
                    ))}
                </ul>

                <button
                    className="absolute left-2 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-3xl shadow-sm disabled:opacity-40"
                    type="button"
                    aria-label="Previous slide"
                    disabled={activeSlide === 0}
                    onKeyDown={handleKeyDown}
                    onClick={() => showSlide(activeSlide - 1)}>
                    <span aria-hidden="true">‹</span>
                </button>
                <button
                    className="absolute right-2 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-3xl shadow-sm disabled:opacity-40"
                    type="button"
                    aria-label="Next slide"
                    disabled={activeSlide === content.slides.length - 1}
                    onKeyDown={handleKeyDown}
                    onClick={() => showSlide(activeSlide + 1)}>
                    <span aria-hidden="true">›</span>
                </button>
            </div>

            <div
                data-aplus-media="desktop"
                className="mx-auto hidden w-full min-[576px]:block"
                style={{ aspectRatio: aspectRatio(content.desktopImage, '4442 / 1788') }}>
                {loadImages ? (
                    <img
                        className="h-auto w-full"
                        src={content.desktopImage.src}
                        alt={content.desktopImage.alt}
                        width={content.desktopImage.width}
                        height={content.desktopImage.height}
                        loading="lazy"
                        decoding="async"
                    />
                ) : null}
            </div>

            <div
                data-aplus-media="detail"
                className="mt-6 w-full"
                style={{ aspectRatio: aspectRatio(content.detailPicture.image, '1 / 1') }}>
                {loadImages ? (
                    <picture className="block">
                        {content.detailPicture.sources.map((source) => (
                            <source
                                key={`${source.media ?? 'all'}-${source.srcSet}`}
                                media={source.media}
                                srcSet={source.srcSet}
                            />
                        ))}
                        <img
                            className="h-auto w-full"
                            src={content.detailPicture.image.src}
                            alt={content.detailPicture.image.alt}
                            width={content.detailPicture.image.width}
                            height={content.detailPicture.image.height}
                            loading="lazy"
                            decoding="async"
                        />
                    </picture>
                ) : null}
            </div>
        </section>
    );
}

function ViewportGatedAPlusContent({ asset }: { asset: APlusContentAsset }) {
    // Activate supplemental media only near the viewport while keeping copy in SSR for SEO.
    const rootRef = useRef<HTMLDivElement>(null);
    const loadImages = useIntersectionObserver(rootRef, { rootMargin: '200px 0px', useOnce: true });

    useEffect(() => {
        if (!loadImages || !rootRef.current || asset.aPlusCarousel) return;

        const activate = (element: Element, attribute: 'src' | 'srcset') => {
            const deferredAttribute = `data-aplus-${attribute}`;
            const value = element.getAttribute(deferredAttribute);
            if (!value) return;
            element.setAttribute(attribute, value);
            element.removeAttribute(deferredAttribute);
        };

        // Restore <source> candidates before the fallback <img> starts its request so the browser
        // selects the correct responsive resource on the first pass.
        for (const source of rootRef.current.querySelectorAll('source')) {
            activate(source, 'src');
            activate(source, 'srcset');
        }
        for (const image of rootRef.current.querySelectorAll('img')) {
            activate(image, 'srcset');
            activate(image, 'src');
        }
    }, [asset.aPlusCarousel, loadImages]);

    if (asset.aPlusCarousel) {
        return (
            // RGBARRY-CUSTOMIZATION: establish a shrinkable containment boundary around structured A+ content.
            <div ref={rootRef} className="w-full min-w-0 max-w-full overflow-x-clip">
                <StructuredCarousel content={asset.aPlusCarousel} loadImages={loadImages} />
            </div>
        );
    }

    const html = asset.html;

    return (
        <div
            ref={rootRef}
            // RGBARRY-CUSTOMIZATION: raw legacy assets may contain fixed widths; contain them within the PDP column.
            className="w-full min-w-0 max-w-full overflow-x-clip aplus-content"
            // oxlint-disable-next-line react/no-danger -- content is sanitized server-side before it reaches this component
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

export default function APlusContent({ asset }: { asset: APlusContentAsset }) {
    // React Router keeps the route mounted across product-param navigations. Key the gated
    // subtree by its server payload so each product gets a fresh observer and activation state.
    const assetKey = asset.aPlusCarousel ? JSON.stringify(asset.aPlusCarousel) : asset.html;
    return <ViewportGatedAPlusContent key={assetKey} asset={asset} />;
}
